import type { ReactNode } from 'react'

import { faHourglassHalf } from '@fortawesome/pro-regular-svg-icons'
import { parseISO } from 'date-fns'
import { styled } from 'styled-components'

import type {
  OrchestratedListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  Duration,
  Banner,
  Button,
  Checkbox,
  FormattedMessage,
  Link,
  LinkAsButton,
  Modal,
  Spinner,
} from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'
import type { PayMethod } from '@b-stock/payments-methods-api-client'

import { CardOrder } from '@/buyer-portal/components/CardOrder'
import { ErrorBannerComponent } from '@/buyer-portal/components/ErrorBanner'
import type { useTotalPaymentInfo } from '@/buyer-portal/components/features/listing/hooks/useTotalPaymentInfo'
import MaxTaxExposureDisplayContainer from '@/buyer-portal/components/features/listing/MaxTaxExposureDisplay'
import MaxVatExposureDisplayContainer from '@/buyer-portal/components/features/listing/MaxVatExposureDisplay'
import PaymentMethodCard from '@/buyer-portal/components/features/order/checkout/PaymentMethods/PaymentMethodCard'
import {
  getDetails,
  getOwner,
  isAchMethod,
} from '@/buyer-portal/components/features/order/checkout/PaymentMethods/utils'
import { useAchSettlementMode } from '@/buyer-portal/helpers/useAchSettlementMode'
import { AchProcessingNotice } from '@/components/AchProcessingNotice'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { ChangeButton, Section, SectionCard, SectionTitle } from '../AutoPay.ui'
import { AutoPayShippingSummary } from './AutoPayShippingSummary'
import {
  BidAmount,
  BidDetailsContent,
  BidDetailsHeader,
  ContentWrapper,
  Description,
  Divider,
  MaxBidTitleAndBidAmount,
  StyledBanner,
  StyledFontAwesomeIcon,
  StyledModalContent,
  TimeLeftSection,
} from './ui'

const ns = 'BidConfirmationModal'

// FF autoPayEnforced: styled components

const AutoPayContentWrapper = styled(ContentWrapper)`
  gap: 1.5rem;
  padding: 0.75rem 1.5rem 0;
  overflow-y: visible;

  @media ${Breakpoints.max.small} {
    padding: 0.75rem 1rem 0;
  }
`

const EnforcedContentWrapper = styled(ContentWrapper)`
  padding: 1rem 1.5rem 0.75rem;
  gap: 1rem;
  overflow-y: visible;

  @media ${Breakpoints.max.small} {
    padding: 1rem 1rem 0.75rem;
    gap: 1rem;
  }
`

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  align-self: stretch;
`

const PaymentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-self: stretch;
`

const NoPmPaymentSection = styled(PaymentSection)`
  gap: 0.75rem;
`

const WireTransferCheckboxWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.25rem;
`

// Pulls divider closer to prevent scrollbar without reducing gap semantics
const NoPmWireTransferCheckboxWrapper = styled(WireTransferCheckboxWrapper)`
  padding-bottom: 0;
  margin-bottom: -0.25rem;
`

// TODO: Add optional `$embedded` prop to PaymentMethodCard to render flat (no border/bg/padding)
// instead of using deep CSS overrides here
const PaymentCardWrapper = styled.div`
  flex: 1 0 0;
  min-width: 0;

  & > div {
    margin-bottom: 0;
    border: none;
    background: transparent;
    padding: 0;
    height: auto;
    /* Card uses min-height for the mobile-wrap case; strip it here so the flat
       embedded card collapses to its content instead of a forced 4.5rem. */
    min-height: auto;

    /* Strip CardDetail margins so SectionCard controls sizing */
    & > div > div {
      margin: 0;
    }
  }
`

// Lets the ACH notice below take a full-width line of its own beneath the card
// and its Change button. Both of those have a zero flex-basis, so they never
// wrap against each other and the row above is unchanged.
const PaymentSectionCard = styled(SectionCard)`
  flex-wrap: wrap;
`

const PaymentAchNotice = styled(AchProcessingNotice)`
  flex-basis: 100%;
`

const WirePaymentSectionCard = styled(SectionCard)`
  flex-wrap: wrap;
`

const PaymentExplanation = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  flex-basis: 100%;
`

// TODO: Move to fe-core — Banner should support compact variant
// with padding: 16px 24px and border-radius: 4px (Figma spec)
const InfoBanner = styled(Banner)`
  padding: 1rem 1.5rem;
  border-radius: 0.25rem;
`

const SlimBanner = styled(Banner)`
  padding: 0.75rem 1rem;
`

const AutoPayConfirmDetailsContent = styled(BidDetailsContent)`
  gap: 1rem;

  @media ${Breakpoints.max.medium} {
    gap: 1rem;
  }

  @media ${Breakpoints.max.small} {
    gap: 1rem;
  }
`

const EnforcedTimeLeftSection = styled(TimeLeftSection)`
  margin-top: 0;

  @media ${Breakpoints.max.medium} {
    padding-bottom: 0;
  }
`

export const FooterContainer = styled(Modal.StyledActionContainer)`
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 0.75rem;
  padding: 1.5rem 3rem;
  border: none;
  border-radius: 0 0 0.5rem 0.5rem;
  ${Shadows.Top}

  @media ${Breakpoints.max.small} {
    padding: 1.5rem 1rem;
  }
`

const FooterText = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export const FooterButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-left: 0;

  @media ${Breakpoints.max.small} {
    flex-direction: column-reverse;
  }
`

export const FooterCancelButton = styled(Button)`
  flex: 0 0 11.9375rem;

  @media ${Breakpoints.max.small} {
    flex: none;
    width: 100%;
  }
`

export const FooterConfirmButton = styled(Button)`
  flex: 1 0 0;
  @media ${Breakpoints.max.small} {
    flex: none;
    width: 100%;
  }
`

export type AutoPayConfirmContentProps = {
  // --- Data ---
  actualEndTime: string | undefined
  currency: string
  listingId: string
  isContract: boolean
  maxBidAmountValue: ReactNode
  // Buy Now's confirm price, so MaxTaxExposureDisplayContainer can resolve
  // the purchase amount instead of the (unrelated, for Buy Now) bid-input value.
  buyNowPrice?: number
  selectedQuote?: OrchestratedQuote | null
  selectedPaymentMethod: PayMethod | null
  termsOfPurchaseUrl?: string
  totalPaymentInfo: ReturnType<typeof useTotalPaymentInfo>

  // --- UI State ---
  enforcedIsConfirmDisabled: boolean
  error: unknown
  hasAnyDigitalMethods: boolean
  isACHBlocked?: boolean
  isBuyNow?: boolean
  isLoading: boolean
  isSavedMethodInvalid?: boolean
  isSelectedMethodRestricted: boolean
  isZeroQuoteValue: boolean
  paymentMethodsLoading?: boolean
  shippingChangePending?: boolean
  isWireSelected: boolean
  showAchBlockedWireFallbackMessage?: boolean
  showCcAcceptedBanner?: boolean

  // --- Actions / Callbacks ---
  groups?: OrchestratedListingDetails['lot']['groups']
  onAddPaymentMethodClick?: () => void
  onCarrierChangeClick?: () => void
  onClose: () => void
  onConfirmBidClick: () => void
  onPaymentMethodClick: () => void
  onShippingChangeClick?: () => void
  onWireTransferChange: () => void
  isCarrierChangeLoading?: boolean
  isCarrierChangeDisabled?: boolean
  hasMultipleCarrierOptions?: boolean
  isCheapestCarrier?: boolean
  // FP-1904: parcel rate list rendered above the CardOrder breakdown when the
  // listing is Binding + Parcel and the parcel-quotes flag is on.
  parcelRateList?: ReactNode
  // FP-1906: when the parcel quote set has no usable rate the rate list is
  // replaced by a blocking banner and Confirm is disabled with a reason.
  parcelPaymentBlocked?: boolean
  // FP-1910: a parcel rate selection is in flight — show the Confirm spinner.
  parcelSelectionPending?: boolean
  // FP-2498 — buyer B-Stock fee (bid × buyersPremium%) in cents, forwarded
  // to MaxTaxExposureDisplayContainer as the BUYER_FEE taxable base. Sourced
  // from `useCalculatedAdditionalCharges.bstockFee` at the parent.
  buyerFeeCents?: number
}

export const AutoPayConfirmContent = ({
  // --- Data ---
  actualEndTime,
  currency,
  listingId,
  isContract,
  maxBidAmountValue,
  buyNowPrice,
  selectedPaymentMethod,
  selectedQuote,
  termsOfPurchaseUrl,
  totalPaymentInfo,
  buyerFeeCents,
  // --- UI State ---
  enforcedIsConfirmDisabled,
  error,
  hasAnyDigitalMethods,
  isBuyNow,
  isACHBlocked = false,
  isLoading,
  isSavedMethodInvalid = false,
  isSelectedMethodRestricted,
  isZeroQuoteValue,
  paymentMethodsLoading,
  shippingChangePending,
  isWireSelected,
  showAchBlockedWireFallbackMessage = false,
  showCcAcceptedBanner,
  // --- Actions / Callbacks ---
  groups,
  onAddPaymentMethodClick,
  onCarrierChangeClick,
  onClose,
  onConfirmBidClick,
  onPaymentMethodClick,
  onShippingChangeClick,
  onWireTransferChange,
  isCarrierChangeLoading,
  isCarrierChangeDisabled,
  hasMultipleCarrierOptions,
  isCheapestCarrier,
  parcelRateList,
  parcelPaymentBlocked = false,
  parcelSelectionPending = false,
}: AutoPayConfirmContentProps) => {
  const { trackButtonClicked } = useAnalytics()
  const achSettlementMode = useAchSettlementMode()

  // A Standard buyer's ACH order is not paid until the transfer clears, so the
  // wait is disclosed on the card that carries the method it applies to. Every
  // other settlement mode, and every non-ACH method, renders no notice. A
  // Restricted ACH method can't complete the purchase at all, so there's no
  // processing wait to disclose.
  const showAchProcessingNotice =
    achSettlementMode === 'settled' &&
    selectedPaymentMethod !== null &&
    isAchMethod(selectedPaymentMethod.type) &&
    !isSelectedMethodRestricted

  const handleAddPaymentMethodClick = onAddPaymentMethodClick
    ? () => {
        trackButtonClicked({ buttonName: 'add_payment_autocharge' })
        onAddPaymentMethodClick()
      }
    : undefined

  const handlePaymentMethodClick = () => {
    trackButtonClicked({
      buttonName: 'edit_current_payment_autocharge',
    })
    onPaymentMethodClick()
  }

  const handleShippingChangeClick = onShippingChangeClick
    ? () => {
        trackButtonClicked({
          buttonName: 'edit_address_autocharge',
        })
        onShippingChangeClick()
      }
    : undefined
  const selectedPaymentMethodIsDefault = !!selectedPaymentMethod?.primaryAt
  // BUGS-5487: a non-USD listing restricts the selected method for a currency/
  // listing reason, not an account-level block — gray out only, matching
  // FP-2418's CC-disabled treatment, instead of the "Restricted" badge.
  const isNonUsdCurrency = !!currency && currency.toUpperCase() !== 'USD'

  // Contract listings defer tax/fee to the fulfilment order regardless of
  // buying format, so the Estimated Total breakdown is hidden for all of them.
  const showEstimatedTotal = !isContract

  // Wire transfer is never auto-charged — either no digital method exists at
  // all (e.g. Canadian listings where only wire is accepted today) or the
  // buyer explicitly chose wire over an available digital method. The
  // "you will be charged" copy only applies to the digital autopay path.
  const willAutoCharge = hasAnyDigitalMethods && !isWireSelected

  return (
    <>
      <StyledModalContent>
        <AutoPayContentWrapper>
          {willAutoCharge && (
            <InfoBanner type="info">
              <FormattedMessage
                id={
                  isBuyNow
                    ? 'BuyNow.BuyNowConfirmationModal.infoBanner'
                    : `${ns}.infoBanner`
                }
              />
            </InfoBanner>
          )}

          <SectionsWrapper>
            <AutoPayShippingSummary
              selectedQuote={selectedQuote}
              onShippingChangeClick={handleShippingChangeClick}
              shippingChangePending={shippingChangePending}
              groups={groups}
              onCarrierChangeClick={onCarrierChangeClick}
              isCarrierChangeLoading={isCarrierChangeLoading}
              isCarrierChangeDisabled={isCarrierChangeDisabled}
              hasMultipleCarrierOptions={hasMultipleCarrierOptions}
              isCheapestCarrier={isCheapestCarrier}
            />

            {paymentMethodsLoading ? (
              <Section>
                <SectionTitle>
                  <FormattedMessage id={`${ns}.payment.title`} />
                </SectionTitle>
                <WireTransferCheckboxWrapper style={{ alignSelf: 'stretch' }}>
                  <Spinner />
                </WireTransferCheckboxWrapper>
              </Section>
            ) : hasAnyDigitalMethods && selectedPaymentMethod ? (
              <Section>
                <SectionTitle>
                  <FormattedMessage id={`${ns}.payment.title`} />
                </SectionTitle>
                <PaymentSectionCard>
                  <PaymentCardWrapper>
                    <PaymentMethodCard
                      name={selectedPaymentMethod.id}
                      type={selectedPaymentMethod.type}
                      owner={getOwner(selectedPaymentMethod)}
                      details={getDetails(selectedPaymentMethod)}
                      value={0}
                      totalAmountRemaining={0}
                      skipAmountValidation
                      isDefault={selectedPaymentMethodIsDefault}
                      defaultTagBackgroundColor={Colors.Semantic.Neutral.white}
                      disableInput={isSelectedMethodRestricted}
                      restrictionReason={
                        isSelectedMethodRestricted
                          ? isNonUsdCurrency
                            ? 'listing_cc_disabled'
                            : 'account_restricted'
                          : undefined
                      }
                    />
                  </PaymentCardWrapper>
                  <ChangeButton
                    appearance="secondary"
                    size="small"
                    type="button"
                    onClick={handlePaymentMethodClick}
                    disabled={paymentMethodsLoading}
                  >
                    <FormattedMessage id={`${ns}.payment.change`} />
                  </ChangeButton>
                  {showAchProcessingNotice ? (
                    <PaymentAchNotice>
                      <FormattedMessage
                        id={
                          isBuyNow
                            ? `${ns}.payment.achNotice.buyNow`
                            : `${ns}.payment.achNotice.auction`
                        }
                        values={{
                          b: (chunks: ReactNode) => <strong>{chunks}</strong>,
                        }}
                      />
                    </PaymentAchNotice>
                  ) : null}
                </PaymentSectionCard>
              </Section>
            ) : hasAnyDigitalMethods && isWireSelected ? (
              <Section>
                <SectionTitle>
                  <FormattedMessage id={`${ns}.payment.title`} />
                </SectionTitle>
                <WirePaymentSectionCard>
                  <PaymentCardWrapper>
                    {/* TODO: PayMethodIcon has no wire icon; brand="bank" (faLandmark) is closest semantic match */}
                    <PaymentMethodCard
                      name="wire"
                      type="wire"
                      owner=""
                      details={{ last4: '', brand: 'bank' }}
                      value={0}
                      totalAmountRemaining={0}
                      skipAmountValidation
                    />
                  </PaymentCardWrapper>
                  <ChangeButton
                    appearance="secondary"
                    size="small"
                    type="button"
                    onClick={handlePaymentMethodClick}
                    disabled={paymentMethodsLoading}
                  >
                    <FormattedMessage id={`${ns}.payment.change`} />
                  </ChangeButton>
                  {showAchBlockedWireFallbackMessage ? (
                    <PaymentExplanation>
                      <FormattedMessage
                        id={`${ns}.payment.achBlockedWireFallback`}
                      />
                    </PaymentExplanation>
                  ) : null}
                </WirePaymentSectionCard>
              </Section>
            ) : !hasAnyDigitalMethods ? (
              <NoPmPaymentSection>
                <SectionTitle>
                  <FormattedMessage id={`${ns}.payment.title`} />
                </SectionTitle>
                <SlimBanner type={isACHBlocked ? 'info' : 'warning'}>
                  <FormattedMessage
                    id={
                      isACHBlocked
                        ? 'BlockedAchAddMethod.reviewOptions'
                        : isBuyNow
                          ? 'BuyNow.BuyNowConfirmationModal.payment.warningBanner'
                          : `${ns}.payment.warningBanner`
                    }
                    values={{
                      link: (chunks: ReactNode) =>
                        handleAddPaymentMethodClick ? (
                          <LinkAsButton
                            type="button"
                            onClick={handleAddPaymentMethodClick}
                          >
                            {chunks}
                          </LinkAsButton>
                        ) : (
                          <span>{chunks}</span>
                        ),
                    }}
                  />
                </SlimBanner>
                <NoPmWireTransferCheckboxWrapper>
                  <Checkbox
                    checked={isWireSelected}
                    onChange={onWireTransferChange}
                    label={
                      <FormattedMessage
                        id={`${ns}.payment.wireTransferCheckbox`}
                      />
                    }
                  />
                </NoPmWireTransferCheckboxWrapper>
              </NoPmPaymentSection>
            ) : (
              <PaymentSection>
                <SectionTitle>
                  <FormattedMessage id={`${ns}.payment.title`} />
                </SectionTitle>
                <SlimBanner type={isACHBlocked ? 'info' : 'warning'}>
                  <FormattedMessage
                    id={
                      isSavedMethodInvalid
                        ? `${ns}.payment.savedMethodInvalid`
                        : `${ns}.payment.selectMethodPrompt`
                    }
                    values={{
                      link: (chunks: ReactNode) => (
                        <LinkAsButton
                          type="button"
                          onClick={() => {
                            trackButtonClicked({
                              buttonName: isSavedMethodInvalid
                                ? 'choose_a_different_method'
                                : 'select_payment_method',
                            })
                            onPaymentMethodClick()
                          }}
                        >
                          {chunks}
                        </LinkAsButton>
                      ),
                    }}
                  />
                </SlimBanner>
              </PaymentSection>
            )}
          </SectionsWrapper>
        </AutoPayContentWrapper>

        <EnforcedContentWrapper>
          {showCcAcceptedBanner && (
            <SlimBanner type="info">
              <FormattedMessage id={`${ns}.creditCardAcceptedBanner`} />
            </SlimBanner>
          )}
          <AutoPayConfirmDetailsContent>
            <div>
              {isContract && (
                <BidDetailsHeader>
                  <MaxBidTitleAndBidAmount>
                    <span>
                      <FormattedMessage
                        id={
                          isBuyNow
                            ? 'BuyNow.BuyNowConfirmationModal.buyNowAmount.title'
                            : `${ns}.yourBidAmt.contract`
                        }
                      />
                    </span>
                    <BidAmount>{maxBidAmountValue}</BidAmount>
                  </MaxBidTitleAndBidAmount>
                  {!isBuyNow && (
                    <Description>
                      <FormattedMessage id={`${ns}.yourBidAmt.description`} />
                    </Description>
                  )}
                </BidDetailsHeader>
              )}
              {/* Suppress the generic LTL/TL "no shipping fee" warning when the
                  parcel no-rates banner is blocking payment (it contradicts the
                  hard block). */}
              {isZeroQuoteValue && !parcelPaymentBlocked && (
                <StyledBanner type="warning">
                  <FormattedMessage id={`${ns}.warning.noShippingFee`} />
                </StyledBanner>
              )}
              {/* FR-009: Contract listings (Auction or Buy Now) defer tax/fee
                  to the fulfilment order and a RELATIVE/percent-of-COGS bid
                  can't be summed into a dollar total at bid-time — mirrors
                  the legacy Auction modal's `!isContract` guard on this same
                  breakdown. */}
              {/* FP-1910: on the parcel path, scope the polite live region to
                  just the order total so a rate switch announces the new total
                  without re-reading the whole rate list. Non-parcel layout is
                  unchanged. */}
              {parcelRateList ? (
                <>
                  {parcelRateList}
                  <div aria-live="polite">
                    {showEstimatedTotal && (
                      <CardOrder
                        data={totalPaymentInfo}
                        level={1}
                        autoCollapseOnDataChange
                        onToggle={() =>
                          trackButtonClicked({
                            buttonName: 'payments_dropdown_autocharge',
                          })
                        }
                      />
                    )}
                  </div>
                </>
              ) : (
                showEstimatedTotal && (
                  <CardOrder
                    data={totalPaymentInfo}
                    level={1}
                    autoCollapseOnDataChange
                    onToggle={() =>
                      trackButtonClicked({
                        buttonName: 'payments_dropdown_autocharge',
                      })
                    }
                  />
                )
              )}
              {showEstimatedTotal && (
                <MaxTaxExposureDisplayContainer
                  listingId={listingId}
                  buyerAddressId={selectedQuote?.destination.id}
                  currencyCode={currency}
                  buyNowPrice={buyNowPrice}
                  buyerFeeCents={buyerFeeCents}
                  shippingCents={selectedQuote?.totalPrice}
                  shipmentType={selectedQuote?.shipmentType}
                />
              )}
              {/* FR-008 EU VAT counterpart. Same showEstimatedTotal gate.
                  Each container self-gates on currency so at most one
                  renders per listing. */}
              {showEstimatedTotal && (
                <MaxVatExposureDisplayContainer
                  listingId={listingId}
                  buyerAddressId={selectedQuote?.destination.id}
                  currencyCode={currency}
                  buyNowPrice={buyNowPrice}
                  buyerFeeCents={buyerFeeCents}
                  shippingCents={selectedQuote?.totalPrice}
                  shipmentType={selectedQuote?.shipmentType}
                />
              )}
              <Divider />
              <EnforcedTimeLeftSection>
                <div>
                  <StyledFontAwesomeIcon icon={faHourglassHalf} />
                  <FormattedMessage id={`${ns}.timeLeft`} />
                </div>
                {actualEndTime ? (
                  <Duration target={parseISO(actualEndTime)} format="short" />
                ) : (
                  <span>N/A</span>
                )}
              </EnforcedTimeLeftSection>
            </div>
          </AutoPayConfirmDetailsContent>

          {error ? <ErrorBannerComponent error={error} /> : null}
        </EnforcedContentWrapper>
      </StyledModalContent>

      <FooterContainer>
        <FooterText>
          <FormattedMessage
            id={
              isBuyNow
                ? 'BuyNow.BuyNowConfirmationModal.check.agree.autopay'
                : `${ns}.check.agree.autopay`
            }
            values={{
              link: (chunks: ReactNode) => (
                <Link target="_blank" href={termsOfPurchaseUrl ?? '#'}>
                  {chunks}
                </Link>
              ),
            }}
          />
        </FooterText>
        <FooterButtonRow>
          <FooterCancelButton
            appearance="secondary"
            disabled={isLoading}
            onClick={onClose}
          >
            <FormattedMessage id="Common.cancel" />
          </FooterCancelButton>
          <FooterConfirmButton
            disabled={enforcedIsConfirmDisabled}
            onClick={onConfirmBidClick}
            appearance={
              isLoading || parcelSelectionPending ? 'loading' : 'primary'
            }
          >
            <FormattedMessage
              id={
                isBuyNow
                  ? 'BuyNow.BuyNowConfirmationModal.footer.confirmBuyNow'
                  : `${ns}.action.confirm`
              }
            />
          </FooterConfirmButton>
        </FooterButtonRow>
      </FooterContainer>
    </>
  )
}
